import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, PhoneCall, Languages, CalendarCheck, UserX, TrendingUp, Clock } from "lucide-react";
import SectionHead from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";

const CATEGORIES = [
  { id: "starter", name: "Starter Solutions", desc: "Suitable for small businesses and startups — a professional website or selected basic automation features, essential enquiry collection, and standard support according to the package." },
  { id: "growth", name: "Growth Solutions", desc: "Suitable for growing businesses — website plus selected AI agent features, lead capture and workflow automation, supported integrations and reporting.", popular: true },
  { id: "enterprise", name: "Enterprise Solutions", desc: "Tailored website and automation requirements, multiple communication channels, advanced integrations, and customized workflows and support agreements." },
];

const PLANS = {
  doctors: {
    label: "Doctors",
    plans: [
      { name: "Advanced", price: 44999 },
      { name: "Ultimate", price: 64999 },
      { name: "Ultimate Fire", price: 72999 },
      { name: "Advanced Pro", price: 99999, popular: true },
      { name: "Ultimate Pro", price: 229999 },
    ],
  },
  clinics: {
    label: "Clinics & Hospitals",
    plans: [
      { name: "Advanced", price: 84999 },
      { name: "Ultimate", price: 99999 },
      { name: "Ultimate Fire", price: 109999 },
      { name: "Advanced Pro", price: 139999, popular: true },
      { name: "Ultimate Pro", price: 229999 },
    ],
  },
  hotels: {
    label: "Hotels",
    plans: [
      { name: "Advanced", price: 49999 },
      { name: "Ultimate", price: 62999 },
      { name: "Ultimate Fire", price: 72999 },
      { name: "Advanced Pro", price: 99999, popular: true },
      { name: "Ultimate Pro", price: 229999 },
    ],
  },
  manufacturing: {
    label: "Manufacturing",
    plans: [
      { name: "Advanced", price: 34999 },
      { name: "Ultimate", price: 54999 },
      { name: "Ultimate Fire", price: 62999 },
      { name: "Advanced Pro", price: 89999, popular: true },
      { name: "Ultimate Pro", price: 135000 },
    ],
  },
};

const PLAN_FEATURES = {
  "Advanced": ["Dynamic website with SSL & your own domain", "E-commerce / booking engine", "Customer enquiries widget & real-time analytics", "Social media marketing, call tracker & IVR", "Google Workspace with business email", "RIA digital assistant & premium support"],
  "Ultimate": ["Everything in Advanced", "GoLive assisted one-time setup", "Banner images & catalogue creation", "Domain, WhatsApp & GMB connection", "One-time platform training"],
  "Ultimate Fire": ["Everything in Ultimate", "Complete business setup bundle", "Business details & logo upload", "IVR registration & VMN connection", "Social media channel connection"],
  "Advanced Pro": ["Everything in Ultimate Fire", "AI Voice Agent — 3,000 calling minutes per year", "Inbound & outbound AI calling", "Higher newsletter subscriber limits", "Dictate assisted content creation (select verticals)"],
  "Ultimate Pro": ["Everything in Advanced Pro", "AI Voice Agent — 25,000 calling minutes per year", "1,000 newsletter subscribers", "Maximum AI automation capacity", "Priority premium support"],
};

const AI_VOICE_PLANS = [
  { minutes: 3000, price: 25000 },
  { minutes: 5000, price: 35000 },
  { minutes: 8000, price: 50000 },
  { minutes: 10000, price: 60000 },
  { minutes: 15000, price: 85000 },
  { minutes: 20000, price: 110000 },
  { minutes: 25000, price: 135000 },
  { minutes: 30000, price: 160000 },
  { minutes: 35000, price: 185000 },
  { minutes: 40000, price: 210000 },
  { minutes: 45000, price: 235000 },
  { minutes: 50000, price: 260000 },
];

const AI_VOICE_FEATURES = [
  { icon: Clock, label: "Handles calls 24/7 — always on your calls" },
  { icon: Languages, label: "Speaks in all Indian languages" },
  { icon: CalendarCheck, label: "Books appointments & captures leads" },
  { icon: PhoneCall, label: "Books & qualifies leads automatically" },
  { icon: UserX, label: "No human resources needed" },
  { icon: TrendingUp, label: "More calls = more business" },
];

const inr = (n) => "₹" + n.toLocaleString("en-IN");
const num = (n) => n.toLocaleString("en-IN");

export default function Pricing() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("doctors");

  useEffect(() => {
    document.title = "Pricing & Packages | RIDGELINE DIGI PARTNER";
  }, []);

  return (
    <div className="pt-[84px]">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <SectionHead eyebrow="PRICING & PACKAGES" title="Choose the Plan That Fits Your Business."
            description="Transparent plans for websites and AI automation — pick an industry plan sheet, or choose an AI Voice Smart Calling Plan based on how many call minutes your business needs." />
          <div className="grid gap-6 md:grid-cols-3">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.id} delay={i * 0.1}>
                <div className={`glass-card rounded-2xl p-7 h-full ${c.popular ? "border-blue-500/50" : ""}`}>
                  <h3 className="font-display text-lg font-semibold text-slate-50">{c.name}</h3>
                  <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{c.desc}</p>
                  <button onClick={() => navigate("/contact")} data-testid={`pricing-request-custom-pricing-${c.id}`}
                    className="mt-5 w-full rounded-full border border-blue-500/50 px-5 py-2.5 text-sm font-semibold text-blue-300 transition-all hover:bg-blue-600 hover:text-white">
                    Request Custom Pricing
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AI VOICE AGENT SMART CALLING PLANS */}
      <section id="ai-voice-plans" className="scroll-mt-24 border-t border-white/5 bg-slate-950/50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <SectionHead eyebrow="AI VOICE AGENT" title="Smart Calling Plans for Your Business"
            description="Get more conversations. Generate more leads. Every missed call is a missed order, appointment, or booking — your AI Voice Agent answers 24/7, speaks every major Indian language, and books and qualifies leads while your team focuses on closing." />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-12">
            {AI_VOICE_FEATURES.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.05}>
                <div className="glass-card rounded-xl px-4 py-4 h-full text-center">
                  <f.icon size={18} className="mx-auto text-cyan-400" />
                  <p className="mt-2.5 text-[11px] leading-snug text-slate-300">{f.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div className="glass-card rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table data-testid="ai-voice-plans-table" className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="border-b border-white/10 bg-blue-600/10">
                      <th className="px-5 py-4 text-left font-mono2 text-[10px] tracking-[0.2em] text-blue-300">MINUTES (PLAN)</th>
                      <th className="px-5 py-4 text-left font-mono2 text-[10px] tracking-[0.2em] text-slate-400">AVG 3-MIN CALLS</th>
                      <th className="px-5 py-4 text-left font-mono2 text-[10px] tracking-[0.2em] text-slate-400">AVG 2-MIN CALLS</th>
                      <th className="px-5 py-4 text-left font-mono2 text-[10px] tracking-[0.2em] text-slate-400">AVG 1-MIN CALLS</th>
                      <th className="px-5 py-4 text-left font-mono2 text-[10px] tracking-[0.2em] text-blue-300">PLAN PRICE</th>
                      <th className="px-5 py-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {AI_VOICE_PLANS.map((p) => (
                      <tr key={p.minutes} className="border-b border-white/5 transition-colors hover:bg-white/[0.03]">
                        <td className="px-5 py-3.5 font-display font-bold text-slate-50">{num(p.minutes)}</td>
                        <td className="px-5 py-3.5 text-slate-300">{num(Math.round(p.minutes / 3))} calls</td>
                        <td className="px-5 py-3.5 text-slate-300">{num(Math.round(p.minutes / 2))} calls</td>
                        <td className="px-5 py-3.5 text-slate-300">{num(p.minutes)} calls</td>
                        <td className="px-5 py-3.5 font-display font-bold text-gradient">{inr(p.price)}</td>
                        <td className="px-5 py-3.5 text-right">
                          <button
                            onClick={() => navigate(`/contact?service=${encodeURIComponent(`AI Voice Agent — ${num(p.minutes)} minutes plan`)}`)}
                            data-testid={`ai-voice-plan-${p.minutes}`}
                            className="rounded-full border border-blue-500/50 px-4 py-1.5 text-xs font-semibold text-blue-300 transition-all hover:bg-blue-600 hover:text-white whitespace-nowrap">
                            Request This Plan
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="px-5 py-4 text-xs text-slate-500 border-t border-white/5">
                Call estimates assume average call durations of 3, 2, or 1 minute. Applicable taxes extra. Calling
                capabilities, languages, integrations, and usage limits depend on the selected plan and technology
                provider. Smart Calls. Bigger Opportunities.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
          <SectionHead eyebrow="INDUSTRY PLAN SHEETS" title="Annual Plans by Industry"
            description="Select your industry to view the current plan sheet. Prices are per year, exclusive of applicable taxes." />
          <div className="flex flex-wrap justify-center gap-2 mb-12" role="tablist" aria-label="Industry">
            {Object.entries(PLANS).map(([key, v]) => (
              <button key={key} role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                data-testid={`pricing-tab-${key}`}
                className={`rounded-full border px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  tab === key
                    ? "border-blue-500 bg-blue-600 text-white shadow-[0_0_20px_rgba(37,99,235,0.4)]"
                    : "border-white/10 bg-slate-900/60 text-slate-300 hover:border-blue-500/50"
                }`}>
                {v.label}
              </button>
            ))}
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {PLANS[tab].plans.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.07}>
                <div className={`relative flex flex-col h-full glass-card rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1.5 ${p.popular ? "border-blue-500/60 shadow-[0_0_44px_rgba(37,99,235,0.22)]" : ""}`}>
                  {p.popular && (
                    <span className="absolute -top-3 left-5 rounded-full bg-blue-600 px-3 py-1 font-mono2 text-[9px] tracking-[0.2em] text-white">POPULAR</span>
                  )}
                  <h3 className="font-display text-lg font-semibold text-slate-50">{p.name}</h3>
                  <p className="mt-3">
                    <span className="font-display text-3xl font-extrabold text-gradient">{inr(p.price)}</span>
                    <span className="text-xs text-slate-500"> /year*</span>
                  </p>
                  <ul className="mt-5 space-y-2.5 flex-1">
                    {PLAN_FEATURES[p.name].map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check size={14} className="mt-0.5 shrink-0 text-cyan-400" />
                        <span className="text-xs leading-relaxed text-slate-300">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => navigate(`/contact?service=${encodeURIComponent(`${PLANS[tab].label} — ${p.name} Plan`)}`)}
                    data-testid={`pricing-plan-${tab}-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`mt-6 w-full rounded-full px-4 py-2.5 text-xs font-semibold transition-all ${
                      p.popular
                        ? "bg-blue-600 text-white hover:bg-blue-500"
                        : "border border-blue-500/50 text-blue-300 hover:bg-blue-600 hover:text-white"
                    }`}>
                    Request Custom Pricing
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8">
            <p className="text-center text-xs text-slate-500 max-w-2xl mx-auto">
              *Applicable taxes extra. Feature availability varies by plan and technology provider. Advanced Pro and
              Ultimate Pro bundle AI Voice Agent calling minutes; higher volumes can be added from the Smart Calling
              Plans above. Contact us for the detailed feature sheet for your industry.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
